import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  HistoricoModificacaoContrato,
  getUsuariosModificadoresPropostaFinanceira,
} from "@/services/consignado/conciliation.service";

interface ModificationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId?: string;
}

const formatHistoryValue = (value: unknown): string => {
  if (value === null || value === undefined)
    return "-";

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
    return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return "[valor invalido]";
  }
};

export default function ModificationHistoryModal({
  isOpen,
  onClose,
  proposalId,
}: ModificationHistoryModalProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modificationHistory, setModificationHistory] = useState<HistoricoModificacaoContrato[]>([]);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen || !proposalId)
      return;

    const loadHistory = async (): Promise<void> => {
      setIsLoading(true);

      try {
        const history = await getUsuariosModificadoresPropostaFinanceira(proposalId);
        setModificationHistory(history);
      } catch (error) {
        console.error("Erro ao buscar usuarios modificadores:", error);
        setModificationHistory([]);
        toast({
          title: "Erro ao buscar historico de modificacoes",
          description: "Nao foi possivel carregar o historico de modificacoes do contrato.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    void loadHistory();
  }, [isOpen, proposalId, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Historico de modificacoes do contrato</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading && (
            <Flex justifyContent="center" py={6}>
              <Spinner size="md" />
            </Flex>
          )}

          {!isLoading && modificationHistory.length === 0 && (
            <Text color="gray.600">Nenhuma modificacao encontrada.</Text>
          )}

          {!isLoading && modificationHistory.length > 0 && (
            <Accordion allowMultiple>
              {modificationHistory.map((historyItem, index) => (
                <AccordionItem
                  key={`${historyItem.usuario}-${index}`}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <h2>
                    <AccordionButton py={3}>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="700">Usuario: {historyItem.usuario}</Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          {historyItem.oQueModificou.length} alteracao(oes)
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>

                  <AccordionPanel pt={0} pb={4}>
                    <Text fontWeight="600" mb={2}>Detalhes das modificacoes:</Text>
                    <VStack spacing={2} align="stretch">
                      {historyItem.oQueModificou.map((modificacao, modificationIndex) => (
                        <Box
                          key={`${historyItem.usuario}-${modificacao.campo}-${modificationIndex}`}
                          bg="gray.50"
                          borderRadius="md"
                          p={2}
                        >
                          <Text fontWeight="600">Campo: {modificacao.campo}</Text>
                          <Text fontSize="sm" color="gray.700">
                            Valor anterior: {formatHistoryValue(modificacao.valorAnterior)}
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            Valor novo: {formatHistoryValue(modificacao.valorNovo)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
